import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Vec2 } from '../../../geo/types';
import { PATTERN } from '../../../render/patterns/patternIds';
import { SURFACE } from '../../mesh/attributes';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { buildVessel } from './churchVessel';
import { buildConventRange } from './conventRange';
import { buildConventEnclosure } from './conventEnclosure';
import { buildConventSoil } from './conventSoil';
import type { LandmarkContext } from './landmarkFeature';
import {
  cellebroedersChapel,
  cellebroedersFloors,
  cellebroedersFrame,
  cellebroedersRangeFrame,
} from './cellebroedersPlan';
import reading from './cellebroedersPlan.json';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf, toLayout } from './planFrame';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

/** Cellebroeders: separate chapel, paired Rokin bodies and lower Nes enclosure. */
export function buildCellebroeders(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & {
    tile: LinearRGB;
    ground: LinearRGB;
    garden: LinearRGB;
  },
  ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY,
  context: Pick<LandmarkContext, 'elevation' | 'elevationAt'> = {}
): void {
  const plan = cellebroedersFrame(landmark),
    frame = meshFrameOf(plan),
    styles = paintedChapelStyles(colors, ids, tracery),
    brick = { ...styles, wall: { ...styles.wall, surface: SURFACE.brick } },
    chapel = cellebroedersChapel();
  buildVessel(builder, brick, frame, chapel, {
    longWindows: { sill: 2.5, top: chapel.eaveY - 1.2 },
    westWindow: true,
    bay: 5.8,
  });
  for (const part of reading.ranges)
    buildConventRange(
      builder,
      meshFrameOf(cellebroedersRangeFrame(plan, part)),
      part.roof === 'tile'
        ? {
            ...brick,
            roof: {
              ...brick.roof,
              color: colors.tile,
              surface: SURFACE.roofTile,
              pattern: PATTERN.roofTileHollePan,
            },
          }
        : brick,
      part
    );
  const base = context.elevation ?? 0,
    terrain = context.elevationAt ?? (() => base),
    localGround = (p: Vec2) => terrain(toLayout(plan, p)) - base;
  buildConventEnclosure(builder, brick.wall, frame, localGround, reading.walls);
  for (const floor of cellebroedersFloors(landmark))
    buildConventSoil(
      builder,
      { ...brick.wall, color: floor.garden ? colors.garden : colors.ground },
      floor.ring,
      terrain,
      base,
      floor.height,
      floor.garden
    );
}
